import React from "react";
import {
  FaExternalLinkAlt,
  FaHandshake,
  FaImage,
  FaListOl,
  FaPen,
  FaTimes,
} from "react-icons/fa";

import {
  getDisplayUrl,
  getMediaUrl,
  getStatusClass,
  getStatusLabel,
} from "../utils/partenairesUtils";

function DetailItem({ label, value, icon: Icon, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          {children || (
            <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
              {value || "—"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PartenaireDetails({ partenaire, onEdit, onClose }) {
  const imageUrl = getMediaUrl(partenaire.photo_url);
  const linkUrl = getDisplayUrl(partenaire.link_url);

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="relative flex h-72 items-center justify-center bg-white p-10 dark:bg-slate-950">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={partenaire.nom}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-slate-400">
              <FaImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucun logo renseigné</p>
            </div>
          )}

          <div className="absolute left-5 top-5">
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                partenaire.is_active
              )}`}
            >
              {getStatusLabel(partenaire.is_active)}
            </span>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
            Partenaire
          </p>
          <h3 className="mt-1 text-3xl font-bold text-slate-950 dark:text-white">
            {partenaire.nom}
          </h3>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem label="Nom" value={partenaire.nom} icon={FaHandshake} />

          <DetailItem
            label="Ordre d'affichage"
            value={partenaire.ordre ?? "—"}
            icon={FaListOl}
          />

          <DetailItem
            label="Statut"
            value={getStatusLabel(partenaire.is_active)}
            icon={FaHandshake}
          />

          <DetailItem label="Lien" icon={FaExternalLinkAlt}>
            {partenaire.link_url ? (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex max-w-full items-center gap-2 break-all text-sm font-semibold text-blue-600 hover:underline dark:text-blue-300"
              >
                <FaExternalLinkAlt className="shrink-0 text-xs" />
                {partenaire.link_url}
              </a>
            ) : (
              <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
                —
              </p>
            )}
          </DetailItem>

          <div className="sm:col-span-2">
            <DetailItem
              label="Logo"
              value={partenaire.photo_url || "Non renseigné"}
              icon={FaImage}
            />
          </div>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}